import { DUPLICATE_STATUS_TYPE } from '@Root/contants/myhouse'
import MyhouseMainHouseResponse from '@Types/entities/MyhouseMainHouseResponse'

export type MyhouseLabelType = {
  houseNo: number
  houseLabel: string
  additionalInfo: string
  fullAdditionalInfo: string
  dupStatus: string
  ownerAuth: string
  residentAuth: string
  ownerAuthCnt: number
  residentAuthCnt: number
  ownerAuthStatus: string
  residentAuthStatus: string
  realestateTypeCode: string
  tradeTypeCode: string
}

export const makeHouseLabel = (houseList: MyhouseMainHouseResponse[]): MyhouseLabelType[] => {
  return houseList.map(
    (house: MyhouseMainHouseResponse): MyhouseLabelType => {
      const complexNo = house.complexNo
      const complexName = house.complexName || ''
      const buildName = house.buildName || ''
      const hoName = house.hoName || ''
      const pyeongName = house.pyeongName || ''
      const baseAddress = house.baseAddress || ''
      const expsAddress = house.expsArea || ''
      const abbrAddress = house.abbrAddress || ''
      const expsAbbrAddress = (!!expsAddress ? expsAddress + ' ' : '') + abbrAddress
      const ownerAuth = house.ownerAuth || ''
      const residentAuth = house.residentAuth || ''
      const ownerAuthStatus = house.ownerAuthStatus || ''
      const residentAuthStatus = house.residentAuthStatus || ''
      const ownerAuthCnt = house.ownerAuthCnt || 0
      const residentAuthCnt = house.residentAuthCnt || 0
      const realestateTypeCode = house.realestateTypeCode || ''
      const tradeTypeCode = house.tradeTypeCode || ''

      const houseNo = house.houseNo
      const dupStatus = getDupStatus(houseList, house.complexNo, house.complexName, house.baseAddress)
      const houseText = makeHouseText(complexNo, complexName, baseAddress, expsAbbrAddress, buildName, hoName, pyeongName, dupStatus)
      const houseLabel = houseText.houseLabel
      const additionalInfo = houseText.additionalInfo
      const fullAdditionalInfo = houseText.fullAdditionalInfo

      return {
        houseNo,
        houseLabel,
        additionalInfo,
        fullAdditionalInfo,
        dupStatus,
        ownerAuth,
        residentAuth,
        ownerAuthCnt,
        residentAuthCnt,
        ownerAuthStatus,
        residentAuthStatus,
        realestateTypeCode,
        tradeTypeCode,
      }
    },
  )
}

type houseTextType = {
  houseLabel: string
  additionalInfo: string
  fullAdditionalInfo: string
}

const makeHouseText = (
  complexNo: number,
  complexName: string,
  baseAddress: string,
  expsAbbrAddress: string,
  buildName: string,
  hoName: string,
  pyeongName: string,
  dupStatus: string,
): houseTextType => {
  const houseText = {
    houseLabel: '',
    additionalInfo: '',
    fullHouseLabel: '',
    fullAdditionalInfo: '',
  }

  // 동일단지 중복 case
  // hscp_no 있고, hscp_nm 있으면, hscp_nm 12자 노출
  if (!!complexNo && !!complexName) {
    houseText.houseLabel = makeHouseLabelTrunc(complexName, 12)
  }
  // hscp_no 있고, hscp_nm 없으면, 주소 12자 노출
  if (!!complexNo && !complexName) {
    houseText.houseLabel = makeHouseLabelTrunc(expsAbbrAddress, 12)
  }
  // hscp_no 중복이고, hscp_nm 있으면, 중복이므로 8자 노출, 동호수 or 면적
  if (!!complexNo && !!complexName && dupStatus === DUPLICATE_STATUS_TYPE.COMPLEX_SEQ_DUP) {
    houseText.houseLabel = makeHouseLabelTrunc(complexName, 8)
    houseText.additionalInfo = makeAdditionalInfo(buildName, hoName, pyeongName, expsAbbrAddress)
  }

  // 동일 건물명 중복 case
  // hscp_no 없고, hscp_nm 있으면, hscp_nm 12자 노출
  if (!complexNo && !!complexName) {
    houseText.houseLabel = makeHouseLabelTrunc(complexName, 12)
  }
  // hscp_no 없고, hscp_nm 없으면, 주소 12자 노출
  if (!complexNo && !complexName) {
    houseText.houseLabel = makeHouseLabelTrunc(expsAbbrAddress, 12)
  }
  // hscp_nm 중복이면
  if (!!complexName && dupStatus === DUPLICATE_STATUS_TYPE.COMPLEX_NAME_DUP) {
    houseText.houseLabel = makeHouseLabelTrunc(complexName, 8)
    houseText.additionalInfo = makeAdditionalInfo(buildName, hoName, pyeongName, '')
  }

  // hscp_no 없고, hcp_nm 없고, base_addr 없으면
  if (!complexNo && !complexName && !baseAddress) {
    houseText.houseLabel = makeHouseLabelTrunc(expsAbbrAddress, 12)
  }
  // hscp_no 없고, hscp_nm 없고, base_addr 있으면
  if (!complexNo && !complexName && !!baseAddress) {
    houseText.houseLabel = makeHouseLabelTrunc(expsAbbrAddress, 12)
  }
  // hscp_no 없고, hcp_nm 없고, base_addr 중복
  if (!complexNo && !complexName && dupStatus === DUPLICATE_STATUS_TYPE.BASE_ADDR_DUP) {
    houseText.additionalInfo = makeAdditionalInfoAddressDup(buildName, hoName, pyeongName, expsAbbrAddress)
    if (!buildName && !hoName && !pyeongName) {
      houseText.houseLabel = makeHouseLabelTrunc(expsAbbrAddress, 12)
    } else {
      houseText.houseLabel = makeHouseLabelTrunc(expsAbbrAddress, 8)
    }
  }

  houseText.fullAdditionalInfo = makeAdditionalInfo(buildName, hoName, pyeongName, '')

  return houseText
}

const makeHouseLabelTrunc = (houseText: string, truncCount: number): string => {
  if (truncCount === 12) {
    if (houseText.length >= 13) {
      return houseText.substr(0, 12).concat('...')
    }
  } else {
    if (houseText.length >= 9) {
      return houseText.substr(0, 8).concat('...')
    }
  }

  return houseText
}

const makeAdditionalInfoAddressDup = (buildName: string, hoName: string, pyeongName: string, abbrAddress: string): string => {
  const buildNameWrap = !!buildName ? buildName + '동 ' : ''
  const hoNameWrap = !!hoName ? hoName + '호' : ''
  const sizeWrap = !!pyeongName ? pyeongName + '㎡' : ''

  const sizeWrapConcat = !buildNameWrap && !hoNameWrap ? sizeWrap : ''

  return buildNameWrap + hoNameWrap + sizeWrapConcat
}

const makeAdditionalInfo = (buildName: string, hoName: string, pyeongName: string, abbrAddress: string): string => {
  const buildNameWrap = !!buildName ? buildName + '동 ' : ''
  const hoNameWrap = !!hoName ? hoName + '호' : ''
  const sizeWrap = !!pyeongName ? pyeongName + '㎡' : ''
  const addrWrap = !!abbrAddress ? abbrAddress : ''

  const sizeWrapConcat = !buildNameWrap && !hoNameWrap ? sizeWrap : ''
  const addrWrapConcat = !buildNameWrap && !hoNameWrap && !pyeongName ? addrWrap : ''

  return buildNameWrap + hoNameWrap + sizeWrapConcat + addrWrapConcat
}

function getDupStatus(houses: MyhouseMainHouseResponse[], complexNo: number, complexName: string, baseAddress: string): string {
  return getComplexSeqDuplicate(houses, complexNo)
    ? DUPLICATE_STATUS_TYPE.COMPLEX_SEQ_DUP
    : getComplexNameDuplicate(houses, complexName)
    ? DUPLICATE_STATUS_TYPE.COMPLEX_NAME_DUP
    : getBaseAddressDuplicate(houses, baseAddress)
    ? DUPLICATE_STATUS_TYPE.BASE_ADDR_DUP
    : DUPLICATE_STATUS_TYPE.NO_DUP
}

function getComplexSeqDuplicate(houses: MyhouseMainHouseResponse[], value: number): boolean {
  const result = houses.filter((item, index) => {
    const houseIndex = houses.map((i) => i.complexNo).indexOf(item.complexNo) !== index
    return houseIndex && !!item.complexNo
  })
  return result.map((item) => item.complexNo).includes(value)
}

function getComplexNameDuplicate(houses: MyhouseMainHouseResponse[], value: string): boolean {
  const result = houses.filter((item, index) => {
    const houseIndex = houses.map((i) => i.complexName).indexOf(item.complexName) !== index
    return houseIndex && !!item.complexName
  })
  return result.map((item) => item.complexName).includes(value)
}

function getBaseAddressDuplicate(houses: MyhouseMainHouseResponse[], value: string): boolean {
  const filterHouses = houses.filter((item) => !item.complexName)
  const result = filterHouses.filter((item, index) => {
    const houseIndex = filterHouses.map((i) => i.baseAddress).indexOf(item.baseAddress) !== index
    return houseIndex && !!item.baseAddress
  })
  return result.map((item) => item.baseAddress).includes(value)
}
